import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { FaArrowLeft, FaClock } from "react-icons/fa";
import { Card, CardContent, CardHeader } from "../misc/Card";
import { Badge } from "../calendar/Badge";
import { Button } from "../misc/Button";
import {
    AlertCircle,
    CalendarIcon,
    Captions,
    Check,
    ImageIcon,
    RefreshCw,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { getApiRefusalMessage } from "../../utils/apiError";
import FacebookPost from "./facebook/FacebookPost";
import InstagramPost from "./instagram/InstagramPost";
import LoadingScreen from "../../screens/LoadingScreen";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FaPencil } from "react-icons/fa6";
import MediaPickerDialog from "./util/MediaPickerDialog";
import { ListingType } from "../../types/userListing";
import { PostRelatedMedia } from "./types";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";
import EditPostDialog from "./util/EditPostDialog";
import RegeneratePostDialog from "./util/RegeneratePostDialog";
import EditSlideshowCaptionsDialog from "./util/EditSlideshowCaptionsDialog";
import EditZapcapCaptionsDialog from "./util/EditZapcapCaptionsDialog";
import { Post as PostType, ZapcapVideoStatus } from "../../types/posts";
import { getAuthHeader } from "../../utils/authHeader";
import CustomToast from "../misc/CustomToast";
import { format, isBefore, setHours, setMinutes } from "date-fns";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../misc/Dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../misc/Popover";
import CustomDatePicker from "../misc/CutomDatePicker";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../misc/Select";
import RandomStatus from "../misc/RandomStatus";
import Spinner from "../misc/Spinner";
import TwitterPost from "./twitter/TwitterPost";
import LinkedInPost from "./linkedin/LinkedInPost";
import { getPlatformIcon } from "../../utils/logo-getter";
import { SocialPlatforms } from "../campaign/types";
import PinterestPost from "./pinterest/PinterestPost";
import GooglePost from "./google/GooglePost";
import { ViewPostButton } from "./ViewPostButton";
import { useSocket } from "../../context/SocketContext";
import PostGraphicPickerDialog from "../misc/custom-posts/PostGraphicPickerDialog";
import PostGraphicsEditor from "../misc/custom-posts/PostGraphicsEditor";
import YouTubePost from "./youtube/YouTubePost";

interface UpdateTimeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (newDateTime: string) => void;
    initialScheduledFor: string;
}

const UpdateTimeDialog: React.FC<UpdateTimeDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialScheduledFor,
}) => {
    // Prefill date with initialScheduledFor if provided; otherwise use current date.
    const initialDate = initialScheduledFor
        ? new Date(initialScheduledFor)
        : new Date();
    const [date, setDate] = useState<Date>(initialDate);
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

    // Store time in 24-hour format
    const initialHour = initialScheduledFor
        ? new Date(initialScheduledFor).getHours()
        : new Date().getHours();
    const initialMinute = initialScheduledFor
        ? new Date(initialScheduledFor).getMinutes()
        : new Date().getMinutes();
    const [time, setTime] = useState<{ hour: number; minute: number }>({
        hour: initialHour,
        minute: initialMinute,
    });

    // When the dialog opens (or initialScheduledFor changes), update state.
    useEffect(() => {
        const newInitialDate = initialScheduledFor
            ? new Date(initialScheduledFor)
            : new Date();
        setDate(newInitialDate);
        setTime({
            hour: newInitialDate.getHours(),
            minute: newInitialDate.getMinutes(),
        });
    }, [initialScheduledFor, isOpen]);

    // Compute period (AM/PM) and display hour from the 24-hour stored value.
    const period = time.hour >= 12 ? "PM" : "AM";
    const displayHour = time.hour % 12 === 0 ? 12 : time.hour % 12;

    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const handleHourChange = (value: string) => {
        const selectedHour12 = parseInt(value, 10);
        let newHour = selectedHour12;
        if (period === "PM" && selectedHour12 !== 12) {
            newHour = selectedHour12 + 12;
        } else if (period === "AM" && selectedHour12 === 12) {
            newHour = 0;
        }
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    const handleMinuteChange = (value: string) => {
        const newMinute = parseInt(value, 10);
        setTime((prev) => ({ ...prev, minute: newMinute }));
    };

    const handlePeriodChange = (value: string) => {
        // Convert the current displayed hour (in 12-hour format) into 24-hour format based on the new period.
        const currentDisplayHour = time.hour % 12 === 0 ? 12 : time.hour % 12;
        const newHour =
            value === "AM"
                ? currentDisplayHour === 12
                    ? 0
                    : currentDisplayHour
                : currentDisplayHour === 12
                    ? 12
                    : currentDisplayHour + 12;
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    const handleSubmit = () => {
        // Combine the selected date and time.
        const scheduledDateTime = setHours(
            setMinutes(new Date(date), time.minute),
            time.hour
        );
        if (isBefore(scheduledDateTime, new Date())) {
            toast.error(
                <CustomToast
                    message="Cannot schedule in the past."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        onSubmit(scheduledDateTime.toISOString());
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="bg-white max-w-md">
                <DialogHeader>
                    <DialogTitle>Update Scheduled Time</DialogTitle>
                    <DialogDescription>
                        The current scheduled time is in the past. Please choose
                        a new date and time.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center justify-center my-4">
                    <div className="flex flex-col gap-4">
                        {/* Date Picker */}
                        <Popover
                            open={isCalendarOpen}
                            onOpenChange={setIsCalendarOpen}
                        >
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-[240px] justify-start text-left"
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {date ? format(date, "PPP") : "Select date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0"
                                align="start"
                            >
                                <CustomDatePicker
                                    date={date}
                                    setDate={setDate}
                                    restriction="month"
                                />
                            </PopoverContent>
                        </Popover>
                        {/* Time Picker */}
                        <div className="flex items-center space-x-2">
                            <Select
                                value={displayHour.toString()}
                                onValueChange={handleHourChange}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Hour" />
                                </SelectTrigger>
                                <SelectContent>
                                    {hourOptions.map((hour) => (
                                        <SelectItem
                                            key={hour}
                                            value={hour.toString()}
                                        >
                                            {hour.toString().padStart(2, "0")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={time.minute.toString()}
                                onValueChange={handleMinuteChange}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Min" />
                                </SelectTrigger>
                                <SelectContent>
                                    {minuteOptions.map((minute) => (
                                        <SelectItem
                                            key={minute}
                                            value={minute.toString()}
                                        >
                                            {minute.toString().padStart(2, "0")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={period}
                                onValueChange={handlePeriodChange}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="AM/PM" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AM">AM</SelectItem>
                                    <SelectItem value="PM">PM</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter className="flex-row justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}>Submit</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type ConfirmAction = "approve" | "pause" | "delete" | null;

interface PostProps {
    postId?: string;
    isDialog?: boolean;
    onClose?: () => void;
}

const Post: React.FC<PostProps> = ({ postId: propPostId, isDialog = false, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { socket, isAuthenticated } = useSocket();
    const { postId: urlPostId } = useParams<{ postId: string }>();
    const [isPostGraphicPickerOpen, setIsPostGraphicPickerOpen] = useState<boolean>(false);
    const [localBgColor, setLocalBgColor] = useState<{
        type: 'solid' | 'gradient';
        color?: string;
        from?: string;
        to?: string;
        direction?: string;
    }>({ type: 'solid', color: '#000000' });
    const [localTextColor, setLocalTextColor] = useState<string>('#f3f4f6');
    const [localFontSize, setLocalFontSize] = useState<number>(24);
    const [localFontFamily, setLocalFontFamily] = useState<string>('Roboto');
    const [hasCustomFontSize, setHasCustomFontSize] = useState<boolean>(false);
    const [localImageText, setLocalImageText] = useState<string>('');

    const videoWatchRef = useRef<number | null>(null);

    useEffect(() => {
        const actualPostId = propPostId || urlPostId || location.state?.postId;
        if (!actualPostId && !isDialog) {
            navigate(-1);
        }
    }, [propPostId, urlPostId, location.state, navigate, isDialog]);

    const actualPostId = propPostId || urlPostId || location.state?.postId;

    const [post, setPost] = useState<PostType | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isSavingMedia, setIsSavingMedia] = useState<boolean>(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] =
        useState<boolean>(false);
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState<boolean>(false);
    // Every media item is a video <=> this is a video post; drives the media
    // picker's video lock and keeps it on the tab dialog (no segments).
    const isVideoPost =
        Array.isArray(post?.media) &&
        post!.media.length > 0 &&
        post!.media.every(
            (m) => m.type === "Listing Video" || m.type === "Asset Video"
        );
    const [isEditCaptionsDialogOpen, setIsEditCaptionsDialogOpen] =
        useState<boolean>(false);
    // The in-flight caption re-render, so its progress can be followed on the slideshow
    // socket (MB-1314).
    const [rerenderJobId, setRerenderJobId] = useState<string | null>(null);
    // MB-1460: ZapCap caption edit — dialog + the in-flight recaption task,
    // followed on its own socket channel (keyed by TASK id, never media url:
    // the completion swaps media[0], which breaks url-keyed listeners).
    const [isEditZapcapCaptionsDialogOpen, setIsEditZapcapCaptionsDialogOpen] =
        useState<boolean>(false);
    const [zapcapRecaptionTaskId, setZapcapRecaptionTaskId] = useState<
        string | null
    >(null);
    // Bumped when transcript-ready arrives so an open dialog refetches.
    const [zapcapTranscriptRefreshToken, setZapcapTranscriptRefreshToken] =
        useState<number>(0);
    const [showDuplicateVideoAlert, setShowDuplicateVideoAlert] = useState<boolean>(false);
    const [media, setMedia] = useState<PostRelatedMedia | null>(null);
    const [postSelectedMedia, setPostSelectedMedia] = useState<string[]>([]);

    const [actionToConfirm, setActionToConfirm] = useState<ConfirmAction>(null);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
    const [isUpdateTimeDialogOpen, setIsUpdateTimeDialogOpen] =
        useState<boolean>(false);

    const [regenerating, setRegenerating] = useState<boolean>(false);

    const handleSaveMedia = async () => {
        setIsSavingMedia(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const selectedMediaData = postSelectedMedia
                .map((id) => {
                    const imageItem = media?.images?.find((m) => m.id === id);
                    if (imageItem) return { id: imageItem.id, url: imageItem.url, type: "Listing Image" };

                    const videoItem = media?.videos?.find((m) => m.id === id);
                    if (videoItem) return { id: videoItem.id, url: videoItem.url, type: "Listing Video" };

                    const assetItem = media?.assets?.find((m) => m.id === id);
                    if (assetItem) return {
                        id: assetItem.id,
                        url: assetItem.url,
                        type: assetItem.assetType?.toLowerCase() === "video" ? "Asset Video" : "Asset Image",
                    };

                    const promotionItem = (media as any)?.promotionImages?.find((m: any) => m.id === id);
                    if (promotionItem) return { id: promotionItem.id, url: promotionItem.url, type: "Promotion" };

                    const archivedItem = media?.archived?.find((m) => m.id === id);
                    if (archivedItem) return {
                        id: archivedItem.id,
                        url: archivedItem.url,
                        type: archivedItem.type === "Removed Video" ? "Asset Video" : "Asset Image",
                    };

                    return undefined;
                })
                .filter(Boolean);

            const response = await axios.patch(
                `${backendUrl}/post`,
                { media: selectedMediaData, postIds: [actualPostId] },
                { headers }
            );

            if (response.status === 200) {
                setIsMediaPickerOpen(false);
                toast.success(
                    <CustomToast message="Media saved successfully." type="success" />,
                    { autoClose: 3000 }
                );
            }

            if (Array.isArray(response.data)) {
                setPost((prev) => (prev ? { ...prev, media: response.data } : prev));
            }
            getPostData();
        } catch (error: unknown) {
            const backendMessage = (error as any)?.response?.data?.message as string | undefined;
            if (backendMessage === "This video is already being used in another YouTube post. Please choose a different video.") {
                setShowDuplicateVideoAlert(true);
            } else {
                toast.error(
                    <CustomToast message={backendMessage || "Failed to save media. Please try again."} type="error" />,
                    { autoClose: 3000 }
                );
            }
        } finally {
            setIsSavingMedia(false);
        }
    };

    const getPostData = useCallback(
        async (load: boolean = true) => {
            if (!actualPostId) return;

            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            if (load) {
                setLoading(true);
            }
            try {
                const response = await axios.get(
                    `${backendUrl}/post/${actualPostId}`,
                    {
                        headers,
                    }
                );

                if (!response || response.status !== 200) {
                    throw new Error("Failed to fetch post data.");
                }

                setPost(response.data);
            } catch (error: unknown) {
                console.error("Error fetching post data", error);
                toast.error(
                    <CustomToast
                        message="Failed to fetch post data."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                if (load) {
                    setLoading(false);
                }
            }
        },
        [actualPostId]
    );

    useEffect(() => {
        getPostData();
    }, [getPostData]);

    // Sync local color state with post data
    useEffect(() => {
        if (post?.background) {
            setLocalBgColor(post.background);
        }
        if (post?.textColor) {
            setLocalTextColor(post.textColor);
        }
        if (post?.fontSize) {
            setLocalFontSize(post.fontSize);
            
            // Check if this is a custom font size (not in preset options)
            const presetFontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72];
            const isCustomFontSize = !presetFontSizes.includes(post.fontSize);
            setHasCustomFontSize(isCustomFontSize);
        }
        if (post?.fontFamily) {
            setLocalFontFamily(post.fontFamily);
        }
        if ((post as any)?.imageText) {
            setLocalImageText((post as any).imageText);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post?.background, post?.textColor, post?.fontSize, post?.fontFamily, (post as any)?.imageText]);

    const updateLeadGenerationPost = useCallback(async (updatedFields: {
        background?: {
            type: 'solid' | 'gradient';
            color?: string;
            from?: string;
            to?: string;
            direction?: string;
        };
        textColor?: string;
        fontSize?: number;
        fontFamily?: string;
        imageText?: string;
        scheduledFor?: string;
    }) => {
        if (!actualPostId) return;

        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const payload: {
                postId: string;
                background?: typeof updatedFields.background;
                textColor?: string;
                fontSize?: number;
                fontFamily?: string;
                scheduledFor?: string;
                imageText?: string;
            } = {
                postId: actualPostId,
            };

            // Add only the fields that are being updated
            if (updatedFields.background !== undefined) {
                payload.background = updatedFields.background;
            }
            if (updatedFields.textColor !== undefined) {
                payload.textColor = updatedFields.textColor;
            }
            if (updatedFields.fontSize !== undefined) {
                payload.fontSize = updatedFields.fontSize;
            }
            if (updatedFields.fontFamily !== undefined) {
                payload.fontFamily = updatedFields.fontFamily;
            }
            if (updatedFields.imageText !== undefined) {
                payload.imageText = updatedFields.imageText;
            }
            if (updatedFields.scheduledFor !== undefined) {
                payload.scheduledFor = updatedFields.scheduledFor;
            }

            const response = await axios.patch(
                `${backendUrl}/lead-generation`,
                payload,
                { headers }
            );

            if (response.status !== 200) {
                throw new Error("Failed to update Lead Generation post.");
            }
        } catch (error) {
            console.error("Error updating Lead Generation post:", error);
            throw error;
        }
    }, [actualPostId]);

    const handleSavePostGraphics = async () => {
        if (!post || !actualPostId) return;
        
        setLoading(true);
        try {
            // For Lead Generation posts, only call the Lead Generation API
            if (post.type === "Lead Generation") {
                await updateLeadGenerationPost({
                    background: localBgColor,
                    textColor: localTextColor,
                    fontSize: localFontSize,
                    fontFamily: localFontFamily,
                    imageText: localImageText,
                });
            }

            toast.success(
                <CustomToast
                    message="Post colors updated successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );

            // Update the post state with new colors
            setPost(prevPost => {
                if (prevPost) {
                    return {
                        ...prevPost,
                        background: localBgColor,
                        textColor: localTextColor,
                        fontSize: localFontSize,
                        fontFamily: localFontFamily,
                    };
                }
                return prevPost;
            });

            // Refresh post data
            getPostData();
        } catch (error: unknown) {
            console.error("Error updating post colors:", error);
            toast.error(
                <CustomToast
                    message="Failed to update post colors. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
            setIsPostGraphicPickerOpen(false);
        }
    };

    const handleBack = () => {
        if (isDialog && onClose) {
            onClose();
        } else {
            navigate(-1);
        }
    };

    // Function to check for [value] placeholders in content
    const validatePlaceholders = useCallback((content: string | string[]): boolean => {
        const placeholderPattern = /\[value\]/gi;
        if (Array.isArray(content)) {
            return !content.some(item => placeholderPattern.test(item));
        }
        return !placeholderPattern.test(content);
    }, []);

    // Memoized callbacks to prevent unnecessary re-renders
    const handleScheduledForChange = useCallback((value: React.SetStateAction<Date>) => {
        const date = typeof value === 'function' ? value(new Date()) : value;
        const isoString = date.toISOString();
        setPost(prev => prev ? { ...prev, scheduledFor: isoString } : prev);
    }, []);

    const handleSaveEdit = useCallback(async (updatedFields: Partial<PostType>) => {
        setLoading(true);
        setIsEditDialogOpen(false);

        if (!actualPostId || !post) {
            toast.error(
                <CustomToast message="Invalid post data." type="error" />,
                { autoClose: 3000 }
            );
            return;
        }

        // Validate that no [value] placeholders remain in the content
        if (updatedFields.content && !validatePlaceholders(updatedFields.content)) {
            toast.error(
                <CustomToast
                    message="Please replace all [value] placeholders in the post content before saving."
                    type="error"
                />,
                { autoClose: 5000 }
            );
            setLoading(false);
            setIsEditDialogOpen(true);
            return;
        }

        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            // Check if this is a Lead Generation post
            if (post.type === "Lead Generation") {
                // For Lead Generation posts, extract all relevant fields including scheduledFor
                const leadGenFields: {
                    background?: typeof post.background;
                    textColor?: string;
                    fontSize?: number;
                    fontFamily?: string;
                    imageText?: string;
                    scheduledFor?: string;
                } = {};
                
                // Map updated fields to Lead Generation API payload (the post has no body text to send)
                if (updatedFields.background !== undefined) {
                    leadGenFields.background = updatedFields.background;
                }
                if (updatedFields.textColor !== undefined) {
                    leadGenFields.textColor = updatedFields.textColor;
                }
                if (updatedFields.fontSize !== undefined) {
                    leadGenFields.fontSize = updatedFields.fontSize;
                }
                if (updatedFields.fontFamily !== undefined) {
                    leadGenFields.fontFamily = updatedFields.fontFamily;
                }
                if ((updatedFields as any).imageText !== undefined) {
                    leadGenFields.imageText = (updatedFields as any).imageText;
                }
                if (updatedFields.scheduledFor !== undefined) {
                    leadGenFields.scheduledFor = updatedFields.scheduledFor;
                }

                // Call Lead Generation API ONLY - no additional post API call
                // Backend will handle schedule validation (reschedule if < 2 min from now)
                if (Object.keys(leadGenFields).length > 0) {
                    await updateLeadGenerationPost(leadGenFields);
                }
            } else {
                // Rescheduling an UNPOSTED post: editing its time alone only saves
                // scheduledFor and leaves it UNPOSTED. Send status APPROVED so the
                // backend re-queues it (UnPosted → Approved → Scheduled), giving the
                // user a working manual reschedule.
                const isReschedulingUnposted =
                    post.status?.toLowerCase() === "unposted" &&
                    updatedFields.scheduledFor !== undefined;

                // For non-Lead Generation posts, call the regular post API
                const response = await axios.patch(
                    `${backendUrl}/post/`,
                    {
                        postIds: [actualPostId],
                        ...updatedFields,
                        ...(isReschedulingUnposted ? { status: "APPROVED" } : {}),
                    },
                    { headers }
                );

                if (!response || response.status !== 200) {
                    throw new Error("Failed to update post.");
                }
            }

            setPost({ ...post, ...updatedFields });
            toast.success(
                <CustomToast
                    message="Post updated successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
            getPostData();
        } catch (error: unknown) {
            console.error("Error updating post", error);
            const backendMessage = (error as any)?.response?.data?.message;
            toast.error(
                <CustomToast message={backendMessage || "Failed to update post."} type="error" />,
                { autoClose: 3000 }
            );
            setIsEditDialogOpen(true);
        } finally {
            setLoading(false);
        }
    }, [actualPostId, post, validatePlaceholders, updateLeadGenerationPost, getPostData]);

    // Memoized callback for PostGraphicsEditor save (declared after handleSaveEdit)
    const handlePostGraphicsEditorSave = useCallback((
        editedImageText: string,
        graphics: {
            background: { type: 'solid' | 'gradient'; color?: string; from?: string; to?: string; direction?: string };
            textColor: string;
            fontSize: number;
            fontFamily: string;
            scheduledFor?: Date;
        }
    ) => {
        // Use values passed directly from the editor — parent state may not have committed yet
        const presetFontSizes = [16, 20, 24, 32, 40, 48];
        setHasCustomFontSize(!presetFontSizes.includes(graphics.fontSize));
        setLocalBgColor(graphics.background);
        setLocalTextColor(graphics.textColor);
        setLocalFontSize(graphics.fontSize);
        setLocalFontFamily(graphics.fontFamily);
        setLocalImageText(editedImageText);

        handleSaveEdit({
            background: graphics.background,
            textColor: graphics.textColor,
            fontSize: graphics.fontSize,
            fontFamily: graphics.fontFamily,
            imageText: editedImageText,
            scheduledFor: graphics.scheduledFor?.toISOString() ?? post?.scheduledFor,
        });
        setIsEditDialogOpen(false);
    }, [handleSaveEdit, post?.scheduledFor]);

    const handleRegenerate = async (instructions: string) => {
        setIsRegenerateDialogOpen(false);
        setRegenerating(true);

        if (!actualPostId || !post) {
            toast.error(
                <CustomToast message="Invalid post data." type="error" />,
                { autoClose: 3000 }
            );
            return;
        }

        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.post(
                `${backendUrl}/post/regenerate/`,
                {
                    postId: actualPostId,
                    text: instructions,
                },
                { headers }
            );

            if (!response || response.status !== 200 || !response.data) {
                throw new Error("Failed to regenerate post content.");
            }

            setPost({ ...post, content: response.data });
            // the save also set or cleared the "written as a thread" flag; reload so the X preview follows it
            getPostData(false);
            toast.success(
                <CustomToast
                    message="Post content regenerated successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
        } catch (error: unknown) {
            console.error("Error regenerating post content", error);
            const fallback = "Failed to regenerate post content.";
            const message = getApiRefusalMessage(error, fallback);
            toast.error(
                <CustomToast message={message} type="error" />,
                { autoClose: message === fallback ? 3000 : 10000 }
            );
        } finally {
            setRegenerating(false);
        }
    };

        const fetchResources = async () => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(
            `${backendUrl}/assets/general-assets`,
            { headers }
        );

        if (!response || response.status !== 200) {
            throw new Error("Failed to fetch resources. Please try again later.");
        }

        // Transform the response to match PostRelatedMedia structure
        return {
            assets: response.data,
        };
    };

    const fetchMediaData = async (postId: string) => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(
            `${backendUrl}/post/all-media/${postId}`,
            {
                headers,
            }
        );

        if (!response || response.status !== 200) {
            throw new Error("Failed to fetch media data.");
        }

        return response.data;
    };

        const handleEditMedia = async () => {
    // Handle Lead Generation posts separately - no loading or fetching needed
    if (post?.type === "Lead Generation") {
        setIsPostGraphicPickerOpen(true);
        return; // Exit early, don't continue with the rest of the function
    }
        setLoading(true);
        try {
            let data;
            if (post?.type === "My Brand") {
                data = await fetchResources();
            } else {
                data = await fetchMediaData(actualPostId);
            }
            setMedia(data);

            // Resolve selected IDs: prefer direct ID match in archived, fall back to URL match
            const postMediaItems = Array.isArray(post?.media) ? post!.media : [];
            const archivedItems: { id: string; url: string }[] = data.archived ?? [];
            const archivedById = new Map(archivedItems.map((a) => [a.id, a]));
            const archivedByUrl = new Map(archivedItems.map((a) => [a.url, a.id]));
            const resolvedIds = postMediaItems.map((m) => {
                if (archivedById.has(m.id)) return m.id; // direct match
                const resolvedByUrl = archivedByUrl.get(m.url);  // URL fallback
                return resolvedByUrl ?? m.id;
            });
            setPostSelectedMedia(resolvedIds);

            setIsMediaPickerOpen(true);
        } catch (error: unknown) {
            console.error("Error fetching media data", error);
            const errorMessage = post?.type === "My Brand" 
                ? "Failed to fetch resources." 
                : "Failed to fetch media data.";
            toast.error(
                <CustomToast
                    message={errorMessage}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }

       
    };

    const isMobile = useIsMobile(900);

    // True for any Caption Video post regardless of status.
    const isCaptionVideo =
        post?.contentType === "Caption Video" || post?.type === "Caption Video";

    // MB-1460: a Caption Video sits in Draft in two very different situations
    // — its video is still being produced, or a failed caption edit parked it
    // for the user to re-review. Only zapCapVideoStatus separates them, so the
    // pending checks below key on it rather than on Draft alone; a parked post
    // has a finished video and must stay fully actionable.
    const isCaptionVideoRendering =
        isCaptionVideo &&
        post?.zapCapVideoStatus === ZapcapVideoStatus.Processing;

    // Lock every action button while the post is still being produced. Covers
    // the Caption Video render (VideoProcessing — ZapCap mid-render, and the
    // Draft pre-render stage a review-mode post waits in) plus the general
    // PROCESSING state any post type sits in right after creation, so the user
    // can't edit, regenerate, or delete a post mid-build.
    const isPostProcessing =
        post?.status?.toLowerCase() === "processing" ||
        post?.status?.toLowerCase() === "videoprocessing" ||
        isCaptionVideoRendering;

    // MB-1460: a Caption Video mid-production has no safe action, and a greyed
    // out row still reads as "try me", so the whole action row is hidden while
    // it is being produced. Scoped to Caption Video - every other post type
    // keeps the disabled-button behaviour it has today.
    const hideCaptionVideoActions = isCaptionVideo && isPostProcessing;

    // Media for a Caption Video isn't ready to preview while it's still being
    // shaped (pre-render, or ZapCap mid-render). This is about the VIDEO only —
    // the YouTube footers shimmer their DESCRIPTION on a separate `!content`
    // check, which is why they can stay on status alone: a post parked here by
    // a failed caption edit always has content (the original completion writes
    // `descriptionDraft || transcriptText`), so their shimmer can't stick.
    const isCaptionVideoMediaPending =
        isCaptionVideoRendering ||
        (isCaptionVideo && post?.status?.toLowerCase() === "videoprocessing");

    // My Brand and Listing AI-disabled posts are always manual flow (no footer, manual char limits).
    // includeFooter is the primary signal, but My Brand posts may have includeFooter=true in the DB
    // from older records, so we also check the post type directly.
    const isManualPost = !post?.includeFooter || post?.type === "My Brand";

    const previewPost: PostType | null = post && isManualPost
        ? { ...post, footer: "", includeFooter: false }
        : post;

    // When ZapCap is still processing, mark all media as isProcessing so each
    // platform component shows its built-in video-processing overlay (limited
    // to the video container only, not the full post card).
    const processingPreviewPost: PostType | null = isCaptionVideoMediaPending && previewPost
        ? {
              ...previewPost,
              media: previewPost.media?.map((m) => ({ ...m, isProcessing: true })),
          }
        : previewPost;

    const confirmAction = async () => {
        if (!actionToConfirm) return;

        if (actionToConfirm === "approve") {
            await handleApprovePost();
        } else if (actionToConfirm === "pause") {
            await handlePausePost();
        } else if (actionToConfirm === "delete") {
            await handleDeletePost();
        }
    };

    // Updated approval handler with scheduledFor check
    const handleApprovePost = async () => {
        // If scheduledFor is in the past, open the update time dialog
        if (post?.scheduledFor && new Date(post.scheduledFor) < new Date()) {
            setIsUpdateTimeDialogOpen(true);
            setIsOverlayOpen(false);
            return;
        }

        setIsOverlayOpen(false);
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.patch(
                `${backendUrl}/post`,
                { postIds: [actualPostId], status: "APPROVED" },
                { headers }
            );

            if (!response || response.status !== 200) {
                throw new Error("Failed to approve post.");
            }

            setPost({ ...post!, status: "Scheduled" });
            getPostData();
            toast.success(
                <CustomToast
                    message="Post approved and scheduled successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
        } catch (error: unknown) {
            console.error("Error approving post", error);
            toast.error(
                <CustomToast message="Failed to approve post." type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    // Called when the user submits a new scheduled time from the dialog
    const handleApprovePostWithNewTime = async (newTime: string) => {
        setIsUpdateTimeDialogOpen(false);
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.patch(
                `${backendUrl}/post`,
                {
                    postIds: [actualPostId],
                    scheduledFor: newTime,
                    status: "APPROVED",
                },
                { headers }
            );

            if (!response || response.status !== 200) {
                throw new Error(
                    "Failed to approve post. Make sure the campaign is active before approving again."
                );
            }

            setPost({ ...post!, status: "Scheduled", scheduledFor: newTime });
            getPostData();
            toast.success(
                <CustomToast
                    message="Post approved and scheduled successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
        } catch (error: unknown) {
            console.error(
                "Error approving post. Make sure the campaign is active before approving again.",
                error
            );
            toast.error(
                <CustomToast
                    message="Error approving post. Make sure the campaign is active before approving again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    const handlePausePost = async () => {
        setIsOverlayOpen(false);
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.patch(
                `${backendUrl}/post`,
                { postIds: [actualPostId], status: "PAUSED" },
                { headers }
            );

            if (!response || response.status !== 200) {
                throw new Error("Failed to pause post.");
            }

            setPost({ ...post!, status: "Paused" });
            toast.success(
                <CustomToast
                    message="Post paused successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
            getPostData();
        } catch (error: unknown) {
            console.error("Error pausing post", error);
            toast.error(
                <CustomToast message="Failed to pause post." type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePost = async () => {
        setIsOverlayOpen(false);
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.delete(
                `${backendUrl}/post/${actualPostId}`,
                { headers }
            );

            if (!response || response.status !== 200) {
                throw new Error("Failed to delete post.");
            }

            toast.success(
                <CustomToast
                    message="Post deleted successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
            navigate(-1);
        } catch (error: unknown) {
            console.error("Error deleting post", error);
            toast.error(
                <CustomToast message="Failed to delete post." type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    const getOverlayContent = () => {
        switch (actionToConfirm) {
            case "approve":
                return {
                    title: "Approve Post",
                    message: "Are you sure you want to approve this Post?",
                    messageTwo:
                        "Approving this post will schedule it for publishing.",
                    confirmText: "Approve Post",
                };
            case "delete":
                return {
                    title: "Delete Post",
                    message: "Are you sure you want to delete this Post?",
                    messageTwo: "This action cannot be undone.",
                    confirmText: "Delete Post",
                };
            default:
                return {
                    title: "",
                    message: "",
                    messageTwo: "",
                    confirmText: "",
                };
        }
    };

    const { title, message, messageTwo, confirmText } = getOverlayContent();

    const handleAction = (action: ConfirmAction) => {
        setActionToConfirm(action);
        setIsOverlayOpen(true);
    };

    const closeOverlay = () => {
        setIsOverlayOpen(false);
        setActionToConfirm(null);
    };

    const stopVideoWatcher = useCallback(() => {
        if (videoWatchRef.current) {
            clearTimeout(videoWatchRef.current);
            videoWatchRef.current = null;
        }
    }, []);

    const startVideoWatcher = useCallback(() => {
        stopVideoWatcher();
        // only poll when we’re still processing
        if (post?.status === "VideoProcessing") {
            videoWatchRef.current = window.setTimeout(() => {
                getPostData(false);
                startVideoWatcher();
            }, 30_000);
        }
    }, [post?.status, getPostData, stopVideoWatcher]);

    useEffect(() => {
        if (!socket || !isAuthenticated) return;

        const video = post?.media?.[0];
        if (!video || post.status !== "VideoProcessing") return;

        // 1) subscribe
        socket.emit("subscribeToVideoConvert", video.url);

        // 2) start the 30s fallback timer
        startVideoWatcher();

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: { currentStep: string } | string;
        }) => {
            if (
                data.entityType === "video-convert" &&
                data.entityId === video.url
            ) {
                // we got a socket update—refresh and restart timer
                getPostData(false);
                startVideoWatcher();
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);

        // cleanup on unmount or deps change
        return () => {
            socket.off("progressUpdate", handleProgressUpdate);
            socket.emit("unsubscribeFromVideoConvert", video.url);
            stopVideoWatcher();
        };
    }, [
        socket,
        isAuthenticated,
        post,
        getPostData,
        startVideoWatcher,
        stopVideoWatcher,
    ]);

    // Silently refresh the post preview whenever the backend signals a post
    // status change (e.g. caption video transitions from DRAFT → SCHEDULED).
    // Mirrors the same "post:updated" listener used in HomeScreen to update the calendar.
    useEffect(() => {
        if (!socket || !isAuthenticated || !actualPostId) return;

        const handlePostUpdated = () => {
            getPostData(false);
        };

        socket.on("post:updated", handlePostUpdated);

        return () => {
            socket.off("post:updated", handlePostUpdated);
        };
    }, [socket, isAuthenticated, actualPostId, getPostData]);

    // MB-1314: follow a caption re-render to completion. The video-convert listener above
    // is keyed on the post's CURRENT media url, which a re-render replaces — so it never
    // fires for one. A slideshow render reports on its own job instead.
    useEffect(() => {
        if (!socket || !isAuthenticated || !rerenderJobId) return;

        const jobId = rerenderJobId;
        socket.emit("subscribeToSlideshow", jobId);

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: string;
        }) => {
            if (data.entityType !== "slideshow" || data.entityId !== jobId) {
                return;
            }
            if (data.progress === "completed") {
                setRerenderJobId(null);
                getPostData(false);
                toast.success(
                    <CustomToast
                        message="Your video has been re-rendered with the new captions."
                        type="success"
                    />,
                    { autoClose: 4000 }
                );
            } else if (data.progress === "failed") {
                setRerenderJobId(null);
                getPostData(false);
                toast.error(
                    <CustomToast
                        message="The video couldn't be re-rendered. Your post is unchanged and still has its original video."
                        type="error"
                    />,
                    { autoClose: 6000 }
                );
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);

        return () => {
            socket.off("progressUpdate", handleProgressUpdate);
            socket.emit("unsubscribeFromSlideshow", jobId);
        };
    }, [socket, isAuthenticated, rerenderJobId, getPostData]);

    // MB-1460: follow a ZapCap caption recaption on its dedicated channel
    // (keyed by the recaption TASK id). Events: transcript-ready (legacy lane
    // — the editor can open on the fresh words), completed, failed.
    useEffect(() => {
        if (!socket || !isAuthenticated || !zapcapRecaptionTaskId) return;

        const taskId = zapcapRecaptionTaskId;
        socket.emit("subscribeToZapcapRecaption", taskId);

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: string;
            reason?: string;
        }) => {
            if (
                data.entityType !== "zapcap-recaption" ||
                data.entityId !== taskId
            ) {
                return;
            }
            if (data.progress === "transcript-ready") {
                // Flips an open dialog from "preparing" to editable; the toast
                // covers the case where they closed it (a notification-centre
                // entry exists too).
                setZapcapTranscriptRefreshToken((n) => n + 1);
                toast.success(
                    <CustomToast
                        message="The transcript is ready - open Edit Captions to review and fix it."
                        type="success"
                    />,
                    { autoClose: 6000 }
                );
            } else if (data.progress === "completed") {
                setZapcapRecaptionTaskId(null);
                getPostData(false);
                toast.success(
                    <CustomToast
                        message="Your corrected captions are live. Every unpublished post using this video has been updated."
                        type="success"
                    />,
                    { autoClose: 5000 }
                );
            } else if (data.progress === "failed") {
                setZapcapRecaptionTaskId(null);
                // An open dialog still on "Preparing…" must refetch to leave it.
                setZapcapTranscriptRefreshToken((n) => n + 1);
                getPostData(false);
                toast.error(
                    <CustomToast
                        message={
                            data.reason
                                ? `The caption update failed (${data.reason}). Your posts are unchanged and keep their original video.`
                                : "The caption update failed. Your posts are unchanged and keep their original video."
                        }
                        type="error"
                    />,
                    { autoClose: 6000 }
                );
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);

        return () => {
            socket.off("progressUpdate", handleProgressUpdate);
            socket.emit("unsubscribeFromZapcapRecaption", taskId);
        };
    }, [socket, isAuthenticated, zapcapRecaptionTaskId, getPostData]);

    return (
        <div
            className={`relative w-full h-full overflow-y-visible overflow-x-hidden
        `}
        >
            {regenerating ? <RandomStatus /> : loading ? <LoadingScreen /> : null}
            <div className={`relative h-full ${isMobile ? "p-2" : "p-4"}`}>

                <ReconfirmOverlay
                    isOpen={isOverlayOpen}
                    onClose={closeOverlay}
                    onConfirm={confirmAction}
                    title={title}
                    message={message}
                    messageTwo={messageTwo}
                    confirmText={confirmText}
                    reverse={actionToConfirm === "delete"}
                />

                {/* Update Time Dialog */}
                <UpdateTimeDialog
                    isOpen={isUpdateTimeDialogOpen}
                    onClose={() => setIsUpdateTimeDialogOpen(false)}
                    onSubmit={handleApprovePostWithNewTime}
                    initialScheduledFor={post?.scheduledFor || ""}
                />

                <Button
                    id="back-btn"
                    onClick={handleBack}
                    aria-label="Go Back"
                    className={`flex items-center mb-4 ${isMobile ? "px-2 py-1 text-sm" : "px-4 py-2"
                        }`}
                >
                    <FaArrowLeft
                        className={`mr-2 ${isMobile ? "w-4 h-4" : "w-5 h-5"}`}
                    />
                    Back
                </Button>

                {post && (
  post.type === "Lead Generation" ? (
    <PostGraphicsEditor
      isOpen={isEditDialogOpen}
      onClose={() => setIsEditDialogOpen(false)}
      onSave={handlePostGraphicsEditorSave}
      bgColor={localBgColor}
      textColor={localTextColor}
      fontSize={localFontSize}
      fontFamily={localFontFamily}
      setBgColor={setLocalBgColor}
      setTextColor={setLocalTextColor}
      setFontSize={setLocalFontSize}
      setFontFamily={setLocalFontFamily}
      imageText={localImageText || (Array.isArray(post.content) ? post.content.join('\n') : post.content || '')}
      setImageText={setLocalImageText}
      previewText={localImageText || (Array.isArray(post.content) ? post.content.join('\n') : post.content || '')}
      scheduledFor={post.scheduledFor ? new Date(post.scheduledFor) : new Date()}
      setScheduledFor={handleScheduledForChange}
      hasCustomFontSize={hasCustomFontSize}
      customFontSize={hasCustomFontSize ? localFontSize : undefined}
    />
  ) : (
    <EditPostDialog
      isOpen={isEditDialogOpen}
      onClose={() => setIsEditDialogOpen(false)}
      post={post}
      onSave={handleSaveEdit}
      setIsOpen={setIsEditDialogOpen}
      bgColor={localBgColor}
      textColor={localTextColor}
      fontSize={localFontSize}
      fontFamily={localFontFamily}
      setBgColor={setLocalBgColor}
      setTextColor={setLocalTextColor}
      setFontSize={setLocalFontSize}
      setFontFamily={setLocalFontFamily}
      hideFooter={isManualPost}
    />
  )
)}

                <RegeneratePostDialog
                    isOpen={isRegenerateDialogOpen}
                    onClose={() => setIsRegenerateDialogOpen(false)}
                    onRegenerate={handleRegenerate}
                />

                {post?.status === "ERROR" ? (
                    <div
                        className={`max-w-${post?.platform === SocialPlatforms.Pinterest
                                ? "5"
                                : "3"
                            }xl mx-auto shadow-lg bg-gray-100 p-3 rounded-md mt-4`}
                    >
                        <p className="text-sm text-gray-600">
                            <strong className="text-[#d50000]">Error :</strong>{" "}
                            Post error is being investigated by our team. We
                            will reach out to you with more information soon.
                        </p>
                        <p className="text-sm text-gray-600">
                            In the meantime, if you have any questions or
                            concerns, please don't hesitate to reach out to our
                            support team through our{" "}
                            <Link
                                className="text-primary hover:text-secondary font-semibold"
                                to="/"
                            >
                                contact form.
                            </Link>
                        </p>
                    </div>
                ) : null}
                <Card
                    className={`max-w-${post?.platform === SocialPlatforms.Pinterest ? "5" : "3"
                        }xl mx-auto shadow-lg ${isMobile ? "w-full" : ""} overflow-x-hidden`}
                >
                    <CardHeader className="space-y-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center flex-wrap gap-2 min-w-0 max-w-full">
                                {post?.scheduledFor && (
                                    <Badge className="flex gap-2 text-sm">
                                        <FaClock className="w-4 h-4" />
                                        {new Date(
                                            post.scheduledFor
                                        ).toLocaleString()}
                                    </Badge>
                                )}
                                {/* MB-1399: developer testing mode — this post
                                    published through the pipeline but never
                                    reached Ayrshare. */}
                                {post?.simulated && (
                                    <Badge className="text-sm px-4">
                                        Simulated
                                    </Badge>
                                )}
                                {post?.contentLength && (
                                    <Badge
                                        className="text-white text-sm px-4"
                                        variant="secondary"
                                    >
                                        {post.contentLength} Post
                                    </Badge>
                                )}
                                {post?.media?.length === 1 && (
                                    <>
                                        {post.status === "VideoProcessing" && (
                                            <Badge
                                                className="text-white text-sm px-4 max-w-full whitespace-normal text-left"
                                                variant="primary"
                                            >
                                                ⏳ Video is still processing.
                                                Please wait...
                                            </Badge>
                                        )}
                                        {post.status === "VideoError" && (
                                            <Badge
                                                className="bg-red-600 text-white text-sm px-4 max-w-full whitespace-normal text-left"
                                                variant="destructive"
                                            >
                                                ❌ Video processing failed.
                                                Please replace the video.
                                            </Badge>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="flex items-center flex-wrap gap-2">
                                {!isMobile &&
                                    getPlatformIcon(post?.platform || "")}
                                {post?.type && (
                                    <Badge
                                        className={`text-white ${isMobile ? "text-xs" : "text-sm"
                                            }`}
                                    >
                                        {post.type}
                                    </Badge>
                                )}
                                {isMobile &&
                                    getPlatformIcon(post?.platform || "")}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-0 overflow-hidden">
                        <div className="space-y-4 overflow-hidden">
                            <div
                                className={`flex justify-between items-center flex-wrap ${isMobile ? "" : "mb-4"
                                    }`}
                            >
                                <div className="w-full flex justify-between items-center">
                                    <div className="flex flex-col">
                                        <h3
                                            className={`text-lg font-semibold text-primary ${isMobile ? "text-md" : "text-lg"
                                                }`}
                                        >
                                            Post Preview
                                            {post?.contentType === "TextPost" ? (
                                                <> - {post.type}</>
                                            ) : (
                                                <>
                                                    {post?.type && post?.address && (
                                                        <> - {post.address}</>
                                                    )}
                                                    {post?.contentType && post?.contentType.replace(/\s+/g, "").toLowerCase() !== post?.address?.replace(/\s+/g, "").toLowerCase() && (
                                                        <>
                                                            {" "}
                                                            -{" "}
                                                            {post.contentType
                                                                .replace(
                                                                    /([A-Z])/g,
                                                                    " $1"
                                                                )
                                                                .replace(/\b\w/g, (c) =>
                                                                    c.toUpperCase()
                                                                )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </h3>
                                        <span className="text-neutral-500 text-sm">
                                            Preview your social media post
                                        </span>
                                    </div>
                                </div>
                                {!hideCaptionVideoActions && (
                                <div className="w-full flex flex-row items-center justify-between">
                                    <div
                                        className={`space-x-2 mt-2 flex flex-row ${post?.status.toLowerCase() ===
                                                "posted"
                                                ? "justify-end"
                                                : post?.type.toLowerCase() ===
                                                    "ad hoc" || post?.type.toLowerCase() === "my brand" || post?.type.toLowerCase() === "lead generation"
                                                    ? "justify-start"
                                                    : post?.platform ===
                                                        SocialPlatforms.Pinterest
                                                        ? "justify-start"
                                                        : "justify-evenly"
                                            }`}
                                    >
                                        {post?.status.toLowerCase() !==
                                            "posted" && !isDialog && (
                                                <Button
                                                    id="edit-post-btn"
                                                    size="sm"
                                                    disabled={isPostProcessing}
                                                    onClick={() =>
                                                        setIsEditDialogOpen(
                                                            true
                                                        )
                                                    }
                                                    className={`flex items-center justify-center ${isMobile
                                                            ? "flex-1"
                                                            : ""
                                                        }`}
                                                >
                                                    <FaPencil className={`w-4 h-4 ${!isMobile ? "mr-2" : ""}`} />
                                                    {!isMobile && "Edit"}
                                                </Button>
                                            )}
                                        {/* MB-1460: a Caption Video in Draft used to mean "still
                                            being produced", so approving made no sense. A failed
                                            caption edit now also parks a FINISHED family in Draft,
                                            and the failure notification tells the user to approve
                                            those. Gate on a Ready render, so a still-rendering post
                                            and a review-held one awaiting its transcript stay
                                            unapprovable from here. */}
                                        {(!isCaptionVideo ||
                                            post?.zapCapVideoStatus ===
                                            ZapcapVideoStatus.Ready) &&
                                            (post?.status.toLowerCase() ===
                                            "draft" ||
                                            post?.status.toLowerCase() ===
                                            "unposted") ? (
                                            <Button
                                                id="approve-post-btn"
                                                variant="secondary"
                                                size="sm"
                                                disabled={isPostProcessing}
                                                onClick={() => handleAction("approve")}
                                                className={`flex items-center justify-center ${isMobile ? "flex-1" : ""}`}
                                            >
                                                <Check className={`w-4 h-4 ${!isMobile ? "mr-2" : ""}`} />
                                                {!isMobile && "Approve Post"}
                                            </Button>
                                        ) : null}

                                        {post?.status.toLowerCase() !==
                                            "posted" && !isDialog && (
                                                <>
                                                    {post?.type.toLowerCase() !==
                                                        "ad hoc" && post?.type.toLowerCase() !==
                                                        "my brand"  && post?.type.toLowerCase() !==
                                                        "lead generation" &&
                                                        post?.contentType !==
                                                        "TextPost" ? (
                                                        <Button
                                                            id="regenerate-post-btn"
                                                            size="sm"
                                                            disabled={isPostProcessing}
                                                            onClick={() =>
                                                                isCaptionVideo
                                                                    ? handleRegenerate("")
                                                                    : setIsRegenerateDialogOpen(true)
                                                            }
                                                            className={`flex items-center justify-center ${isMobile
                                                                    ? "flex-1"
                                                                    : ""
                                                                }`}
                                                        >
                                                            <RefreshCw
                                                                className={`w-4 h-4 ${isMobile
                                                                        ? "m-0"
                                                                        : "mr-2"
                                                                    }`}
                                                            />
                                                            {!isMobile &&
                                                                "Regenerate"}
                                                        </Button>
                                                    ) : null}
                                                    {/* MB-1314: only an automatically generated
                                                    video post carries a slideshowJobId, so this
                                                    appears for exactly those posts. */}
                                                    {post?.slideshowJobId && (
                                                        <Button
                                                            id="edit-slideshow-captions-btn"
                                                            size="sm"
                                                            variant="secondary"
                                                            disabled={isPostProcessing}
                                                            onClick={() =>
                                                                setIsEditCaptionsDialogOpen(
                                                                    true
                                                                )
                                                            }
                                                            className={`flex items-center justify-center ${
                                                                isMobile
                                                                    ? "flex-1"
                                                                    : ""
                                                            }`}
                                                        >
                                                            <Captions
                                                                className={`w-4 h-4 ${
                                                                    isMobile
                                                                        ? "m-0"
                                                                        : "mr-2"
                                                                }`}
                                                            />
                                                            {!isMobile &&
                                                                "Edit Captions"}
                                                        </Button>
                                                    )}
                                                    {/* MB-1460: post-hoc caption editing for Caption
                                                    Video (ZapCap) posts — same slot/pattern as the
                                                    MB-1314 slideshow button above. Gated on the
                                                    retained original video (every family post
                                                    carries it) and a finished original render;
                                                    the surrounding block already excludes Posted,
                                                    and isPostProcessing locks it mid-render. */}
                                                    {isCaptionVideo &&
                                                        post?.zapcapOriginalVideoUrl &&
                                                        post?.zapCapVideoStatus ===
                                                            ZapcapVideoStatus.Ready && (
                                                            <Button
                                                                id="edit-zapcap-captions-btn"
                                                                size="sm"
                                                                variant="secondary"
                                                                disabled={isPostProcessing}
                                                                onClick={() =>
                                                                    setIsEditZapcapCaptionsDialogOpen(
                                                                        true
                                                                    )
                                                                }
                                                                className={`flex items-center justify-center ${
                                                                    isMobile
                                                                        ? "flex-1"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <Captions
                                                                    className={`w-4 h-4 ${
                                                                        isMobile
                                                                            ? "m-0"
                                                                            : "mr-2"
                                                                    }`}
                                                                />
                                                                {!isMobile &&
                                                                    "Edit Captions"}
                                                            </Button>
                                                        )}
                                                    {/* MB-1314: an auto-generated video post's media IS its
                                                    rendered slideshow — swapping it would orphan the
                                                    captions the video was built from. Its captions are
                                                    editable instead (Edit Captions, above). */}
                                                    {!isCaptionVideo &&
                                                        !post?.slideshowJobId &&
                                                        post?.status.toLowerCase() !==
                                                        "posted" &&
                                                        !isDialog &&
                                                        post?.type.toLowerCase() !==
                                                            "lead generation" &&
                                                        post?.contentType !==
                                                            "TextPost" && (
                                                            <Button
                                                                id="edit-media-btn"
                                                                size="sm"
                                                                variant="secondary"
                                                                disabled={isPostProcessing}
                                                                onClick={
                                                                    handleEditMedia
                                                                }
                                                                className={`flex items-center justify-center ${
                                                                    isMobile
                                                                        ? "flex-1"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <ImageIcon
                                                                    className={`w-4 h-4 ${
                                                                        isMobile
                                                                            ? "m-0"
                                                                            : "mr-2"
                                                                    }`}
                                                                />
                                                                {!isMobile &&
                                                                    "Edit Media"}
                                                            </Button>
                                                        )}
                                                </>
                                            )}
                                        {!isDialog &&
                                            post?.status.toLowerCase() ===
                                                "posted" &&
                                            post?.postUrl && (
                                                <ViewPostButton
                                                    postUrl={post.postUrl}
                                                    platform={post.platform}
                                                    isMobile={isMobile}
                                                />
                                            )}
                                    </div>
                                    {/* Right group: Delete Post */}
                                    {!isDialog && (
                                        <Button
                                            id="delete-post-btn"
                                            variant="destructive"
                                            size="sm"
                                            disabled={isPostProcessing}
                                            onClick={() => handleAction("delete")}
                                            className="flex items-center justify-center"
                                        >
                                            <Trash2 className={`w-4 h-4 ${!isMobile ? "mr-2" : ""}`} />
                                            {!isMobile && "Delete Post"}
                                        </Button>
                                    )}
                                </div>
                                )}
                            </div>
                            {post?.status === "VideoProcessing" ? (
                                <div className="flex flex-col items-center justify-center gap-6 py-16 text-center">
                                    <Spinner />
                                    <p className="text-sm font-medium text-primary">
                                        Your video is still processing. Please
                                        wait...
                                    </p>
                                </div>
                            ) : post &&
                                post.platform === SocialPlatforms.Facebook ? (
                                <FacebookPost
                                    post={processingPreviewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post &&
                                post.platform === SocialPlatforms.Instagram ? (
                                <InstagramPost
                                    post={processingPreviewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post &&
                                post.platform === SocialPlatforms.Twitter ? (
                                <TwitterPost
                                    post={processingPreviewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post &&
                                post?.platform === SocialPlatforms.LinkedIn ? (
                                <LinkedInPost
                                    post={processingPreviewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post &&
                                post?.platform === SocialPlatforms.Pinterest ? (
                                <PinterestPost
                                    post={previewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post?.platform === SocialPlatforms.Google ? (
                                <GooglePost
                                    post={previewPost!}
                                    setLoading={setLoading}
                                />
                            ) : post?.platform === SocialPlatforms.YouTube ? (
                                <YouTubePost
                                    post={processingPreviewPost!}
                                />
                            ) : null}
                        </div>
                    </CardContent>

                    <EditSlideshowCaptionsDialog
                        isOpen={isEditCaptionsDialogOpen}
                        onClose={() => setIsEditCaptionsDialogOpen(false)}
                        postId={actualPostId}
                        onRerenderStarted={(jobId) => {
                            setRerenderJobId(jobId);
                            // The spinner and the action-button lock are already wired to
                            // VideoProcessing, so flipping it here makes the screen react
                            // immediately rather than waiting for the refetch.
                            setPost((prev) =>
                                prev
                                    ? { ...prev, status: "VideoProcessing" }
                                    : prev
                            );
                            getPostData(false);
                        }}
                    />

                    <EditZapcapCaptionsDialog
                        isOpen={isEditZapcapCaptionsDialogOpen}
                        onClose={() =>
                            setIsEditZapcapCaptionsDialogOpen(false)
                        }
                        postId={actualPostId}
                        refreshToken={zapcapTranscriptRefreshToken}
                        onRecaptionStarted={(taskId, legacy) => {
                            setZapcapRecaptionTaskId(taskId);
                            if (!legacy) {
                                // The re-render is underway: flip optimistically so
                                // the spinner + action lock react immediately; the
                                // refetch confirms.
                                setPost((prev) =>
                                    prev
                                        ? {
                                              ...prev,
                                              status: "VideoProcessing",
                                          }
                                        : prev
                                );
                                getPostData(false);
                            }
                            // legacy prepare = transcription only; the post is
                            // untouched until the user resubmits.
                        }}
                    />

                    <MediaPickerDialog
                        isOpen={isMediaPickerOpen}
                        onClose={() => setIsMediaPickerOpen(false)}
                        onNext={handleSaveMedia}
                        media={media || { images: [], videos: [], assets: [] }}
                        selectedMediaIds={postSelectedMedia}
                        setSelectedMediaIds={setPostSelectedMedia}
                        isMobile={isMobile}
                        platform={post?.platform || ""}
                        listingId=""
                        listingType={post?.contentType as ListingType | undefined}
                        fetchMedia={async () => {
                            const data = await fetchMediaData(actualPostId);
                            setMedia(data);
                        }}
                        uploadsEnabled={false}
                        isVideo={isVideoPost}
                        initialMediaType={isVideoPost ? "video" : "image"}
                        postType={post?.type}
                        isNext={false}
                        sharedMediaOnly={post?.type?.toLowerCase() === "my brand"}
                        mediaTypeSegments={!isVideoPost && post?.type !== "Promotion"}
                        isSaving={isSavingMedia}
                    />

                    <Dialog open={showDuplicateVideoAlert} onOpenChange={setShowDuplicateVideoAlert}>
                        <DialogContent className="max-w-sm p-6 flex flex-col items-center gap-4 text-center bg-white">
                            <DialogClose className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                                <X className="h-4 w-4" />
                            </DialogClose>
                            <AlertCircle className="h-10 w-10 text-yellow-500" />
                            <p className="text-sm text-gray-800">
                                This video has already been used for a YouTube post. Please select a different video.
                            </p>
                            <Button
                                className="bg-primary text-primary-foreground hover:bg-primary/90"
                                onClick={() => setShowDuplicateVideoAlert(false)}
                            >
                                Close
                            </Button>
                        </DialogContent>
                    </Dialog>

                    <PostGraphicPickerDialog
                        open={isPostGraphicPickerOpen}
                        onClose={() => setIsPostGraphicPickerOpen(false)}
                        onSave={handleSavePostGraphics}
                        isMobile={isMobile}
                        generalInstructions={post?.content || [""]}
                        bgColor={localBgColor}
                        textColor={localTextColor}
                        fontSize={localFontSize}
                        fontFamily={localFontFamily}
                        setBgColor={setLocalBgColor}
                        setTextColor={setLocalTextColor}
                        setFontSize={setLocalFontSize}
                        setFontFamily={setLocalFontFamily}
                    />
                </Card>
            </div>
        </div>
    );
};

export default Post;
